import { ArrowUp, Mic, Plus } from "lucide-react";
import type { FormEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { useRevealReducedMotion } from "@/lib/reveal";

interface ChatInputProps {
  onSend: (message: string) => void;
  /** Fixed lead-in shown before the cycling prompt — never animates away. */
  prefix: string;
  /** Suffixes cycled through the typewriter placeholder. */
  phrases: string[];
  /** Accessible name for the field (the animated placeholder is decorative). */
  label: string;
  /** Accessible name for the submit button. */
  sendLabel: string;
  /** Accessible name for the attach (+) button. */
  attachLabel: string;
  /** Accessible name for the voice button. */
  voiceLabel: string;
}

// Typewriter timings (ms). Deliberately unhurried so each prompt stays readable.
const TYPE_MS = 55;
const DELETE_MS = 28;
const HOLD_MS = 1600; // dwell on a fully-typed phrase before erasing
const GAP_MS = 400; // dwell at the empty suffix before the next phrase

// Cycles a suffix through type-in → hold → delete-out phases and returns the
// currently-visible slice. Frozen (and reset to empty) while `active` is false,
// so no timers run behind a field the visitor has already typed into.
function useTypewriter(phrases: string[], active: boolean): string {
  const [visible, setVisible] = useState("");
  // Read the list from a ref so a fresh array identity each render never resets
  // the in-flight animation — only a real content change (the joined key) does.
  const phrasesRef = useRef(phrases);
  phrasesRef.current = phrases;
  const phrasesKey = phrases.join("");

  // biome-ignore lint/correctness/useExhaustiveDependencies: phrases read via ref; phrasesKey captures real changes
  useEffect(() => {
    if (!active) {
      setVisible("");
      return;
    }
    let timer: ReturnType<typeof setTimeout>;
    let phraseIndex = 0;
    let len = 0;
    let deleting = false;

    const tick = () => {
      const list = phrasesRef.current;
      const full = list[phraseIndex % Math.max(list.length, 1)] ?? "";
      if (!deleting) {
        len += 1;
        setVisible(full.slice(0, len));
        deleting = len >= full.length;
        timer = setTimeout(tick, deleting ? HOLD_MS : TYPE_MS);
      } else {
        len -= 1;
        setVisible(full.slice(0, Math.max(len, 0)));
        if (len <= 0) {
          deleting = false;
          phraseIndex += 1;
          timer = setTimeout(tick, GAP_MS);
        } else {
          timer = setTimeout(tick, DELETE_MS);
        }
      }
    };

    timer = setTimeout(tick, TYPE_MS);
    return () => clearTimeout(timer);
  }, [active, phrasesKey]);

  return visible;
}

const ChatInput = ({
  onSend,
  prefix,
  phrases,
  label,
  sendLabel,
  attachLabel,
  voiceLabel,
}: ChatInputProps) => {
  const [input, setInput] = useState("");
  const reducedMotion = useRevealReducedMotion();

  const isEmpty = input.trim() === "";
  const animate = isEmpty && !reducedMotion;
  const typed = useTypewriter(phrases, animate);
  // Reduced-motion still deserves a concrete example — show the first one whole.
  const suffix = reducedMotion ? (phrases[0] ?? "") : typed;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (trimmed) onSend(trimmed);
  };

  // Conversion-only landing composer: auxiliary controls currently take the
  // same launch/waitlist handoff as send. They do not implement attachment or
  // speech input here; the promise audit tracks that visible-label mismatch.
  const handleAuxiliary = () => onSend(input.trim());

  return (
    /* Lovable-style two-row composer: input on top, action row below. */
    <form onSubmit={handleSubmit} className="liquid-glass min-h-[132px] rounded-[28px] bg-black/30">
      <div className="flex h-full min-h-[132px] flex-col justify-between p-3">
        {/* The native placeholder can't hold a fixed prefix + animated suffix, so
            it's an aria-hidden overlay the real (transparent-placeholder) input
            sits on top of. It shows only while the field is empty. */}
        <div className="relative px-2 pt-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label={label}
            className="w-full bg-transparent text-base text-foreground outline-none"
          />
          {isEmpty && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-nowrap px-2 pt-2 text-base text-muted-foreground"
            >
              {/* One text run so the space after the fixed prefix survives — split
                  across flex items, CSS trims it at each item's line-box edge. */}
              <span>
                {prefix}
                {suffix}
                {animate && (
                  <span className="ml-px inline-block h-[1.1em] w-px animate-caret-blink bg-muted-foreground align-middle" />
                )}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleAuxiliary}
            aria-label={attachLabel}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-border text-muted-foreground transition-colors duration-300 hover:border-white/25 hover:text-foreground"
          >
            <Plus className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAuxiliary}
              aria-label={voiceLabel}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors duration-300 hover:text-foreground"
            >
              <Mic className="h-4 w-4" />
            </button>
            <button
              type="submit"
              disabled={isEmpty}
              aria-label={sendLabel}
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground transition-all duration-300 hover:scale-110 hover:bg-primary-press active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ChatInput;
